import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { kpiEntity } from '@/config/entities/kpis';
import { createCRUDService } from '@/services/genericCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import { KPICard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { KpiPackDownload } from '@/components/kpis/KpiPackDownload';
import { KpiTenantThresholds } from '@/components/kpis/KpiTenantThresholds';
import { KpiBulkEditBar } from '@/components/kpis/KpiBulkEditBar';
import {
  EMPTY_KPI_CATALOG_FILTERS,
  KpiCatalogFilterPanel,
  KpiCatalogFilterToggle,
  kpiFiltersToQueryParams,
  type KpiCatalogFilterValues
} from '@/components/kpis/KpiCatalogFilters';
import toast from 'react-hot-toast';
import { useCardViewRefresh } from '@/hooks/useCardViewRefresh';

const kpiService = createCRUDService(kpiEntity);

/**
 * Tenant KPI catalog (definitions). On-demand filters sit beside Search
 * (same pattern as other FlexibleCRUD catalogs).
 */
export const KPIsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('kpis-view-mode');
    return saved === 'cards' || saved === 'table' ? saved : 'cards';
  });

  const [cardData, setCardData] = useState<any[]>([]);
  const [filteredCardData, setFilteredCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [filters, setFilters] = useState<KpiCatalogFilterValues>(EMPTY_KPI_CATALOG_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { refreshKey, refresh } = useCardViewRefresh(viewMode);
  const extraQueryParams = useMemo(() => kpiFiltersToQueryParams(filters), [filters]);

  useEffect(() => {
    localStorage.setItem('kpis-view-mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (viewMode === 'cards') {
      loadCardData();
    }
  }, [viewMode, location.pathname, refreshKey, extraQueryParams]);

  const loadCardData = async () => {
    try {
      setCardLoading(true);
      const response = await kpiService.getAll({
        limit: 2000,
        ...extraQueryParams
      });
      const kpis = response.data || [];
      setCardData(kpis);
      applySearchAndSort(kpis, searchTerm, sortBy);
    } catch (error) {
      console.error('Error loading KPIs for card view:', error);
      toast.error(t('kpi.errorLoadingKPIs') || 'Error loading KPIs');
    } finally {
      setCardLoading(false);
    }
  };

  const applySearchAndSort = (data: any[], search: string, sort: string) => {
    let filtered = [...data];

    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower)
      );
    }

    switch (sort) {
      case 'date-newest':
        filtered.sort(
          (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
        break;
      case 'name':
      default:
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
    }

    setFilteredCardData(filtered);
  };

  useEffect(() => {
    if (viewMode === 'cards' && cardData.length > 0) {
      applySearchAndSort(cardData, searchTerm, sortBy);
    }
  }, [searchTerm, sortBy, cardData, viewMode]);

  const handleView = (item: any) => {
    navigate(`/kpis/${item.id}`);
  };

  const handleEdit = (item: any) => {
    navigate(`/kpis/${item.id}/edit`);
  };

  const handleCreate = () => {
    navigate('/kpis/create');
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      await kpiService.toggleStatus(id, isActive);
      toast.success(
        isActive
          ? t('kpi.statusNowActive') || 'KPI is active'
          : t('kpi.statusNowInactive') || 'KPI is inactive'
      );
      refresh();
    } catch (error) {
      console.error('Error changing KPI status:', error);
      toast.error(t('kpi.statusChangeError') || 'Failed to change KPI status');
    }
  };

  const handleDelete = async (item: any) => {
    if (window.confirm(t('kpi.confirmDelete', { name: item.name }))) {
      try {
        await kpiService.delete(item.id);
        toast.success(t('kpi.deleteSuccess'));
        refresh();
      } catch (error) {
        console.error('Error deleting KPI:', error);
        toast.error(t('kpi.deleteError'));
      }
    }
  };

  const filterToggle = (
    <KpiCatalogFilterToggle
      open={filtersOpen}
      onOpenChange={setFiltersOpen}
      value={filters}
      onChange={setFilters}
    />
  );
  const filterPanel = filtersOpen ? (
    <KpiCatalogFilterPanel value={filters} onChange={setFilters} />
  ) : null;

  const handleBulkApplied = () => {
    setSelectedIds([]);
    refresh();
    if (viewMode === 'cards') {
      loadCardData();
    }
  };

  const toggleCardSelected = (id: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) return current.includes(id) ? current : [...current, id];
      return current.filter((value) => value !== id);
    });
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          <KpiPackDownload onApplied={refresh} />
          <div className="mb-2">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-1">KPIs</h1>
                <p className="text-xs text-[var(--color-secondary-text)]">{t('kpi.manageKPIs')}</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/kpis/loads"
                  className="text-xs text-[var(--color-primary-text)] hover:underline"
                >
                  {t('kpi.loadTitle')}
                </Link>
                <div className="flex items-center gap-1 bg-[var(--color-secondary-bg)] rounded-lg p-1">
                  <ViewSwitcher currentView={viewMode} onViewChange={setViewMode} className="bg-transparent p-0" />
                  <KpiTenantThresholds />
                </div>
                {viewMode === 'table' && (
                  <button
                    onClick={handleCreate}
                    className="px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                  >
                    {t('kpi.createKPI') || 'Create KPI'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {viewMode === 'table' ? (
            <div className="mt-6">
              <KpiBulkEditBar
                selectedIds={selectedIds}
                filters={filters}
                onApplied={handleBulkApplied}
              />
              <FlexibleCRUDTable
                key={JSON.stringify(extraQueryParams)}
                entity={kpiEntity}
                service={kpiService}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                onCreate={handleCreate}
                searchPlaceholder={t('kpi.searchPlaceholder')}
                showPagination={true}
                showSearch={true}
                extraQueryParams={extraQueryParams}
                toolbarAfterSearch={filterToggle}
                belowToolbar={filterPanel}
                enableRowSelection
                onSelectionChange={setSelectedIds}
              />
            </div>
          ) : (
            <div className="mt-6">
              <FilterToolbar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder={t('kpi.searchPlaceholder') || 'Search KPIs...'}
                showFilters={false}
                toolbarAfterSearch={filterToggle}
                sortOptions={[
                  { id: 'name', label: 'Name (A-Z)', value: 'name' },
                  { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' }
                ]}
                sortValue={sortBy}
                onSortChange={setSortBy}
              />
              {filterPanel}
              <div className="mt-2">
                <KpiBulkEditBar
                  selectedIds={selectedIds}
                  filters={filters}
                  search={searchTerm}
                  onApplied={handleBulkApplied}
                />
              </div>

              {cardLoading ? (
                <div className="text-center py-8 mt-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
                </div>
              ) : filteredCardData.length === 0 ? (
                <div className="text-center py-8 mt-6">
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                    {cardData.length === 0
                      ? t('kpi.noKpisFound') || 'No KPIs found'
                      : t('kpi.noKpisMatch') || 'No KPIs match your search'}
                  </p>
                </div>
              ) : (
                <div className="mt-6">
                  <div className="mb-2 flex items-center gap-2 text-xs text-[var(--color-secondary-text)]">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={
                          filteredCardData.length > 0 &&
                          filteredCardData.every((kpi) => selectedIds.includes(kpi.id))
                        }
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedIds(filteredCardData.map((kpi) => kpi.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                      {t('common.selectAll') || 'Select all visible'}
                    </label>
                  </div>
                  <CardGrid columns={4}>
                    {filteredCardData.map((kpi) => (
                      <KPICard
                        key={kpi.id}
                        kpi={kpi}
                        selected={selectedIds.includes(kpi.id)}
                        onSelectedChange={(checked) => toggleCardSelected(kpi.id, checked)}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                      />
                    ))}
                  </CardGrid>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
